import type { IFileItem } from '../models/FileItem';
import type { IFolderItem } from '../models/FolderItem';
import type {
  IChangeDecision, IOrganizationPlan, IPlanConflict, IPlannedChange, IPlanPreview, IPreviewEntry, PreviewMark
} from '../models/Plan';
import type { IScanResult } from '../models/ScanResult';
import { filePath, folderLabel, values } from '../rules/ruleUtils';
import { validateItemName } from './names';

/** A change with the admin's edits applied and validated. */
export interface IResolvedChange {
  change: IPlannedChange;
  status: IChangeDecision['status'];
  /** renameFile / fileDocument */
  newName?: string;
  /** moveFolder / fileIntake: destination folder path, '' for the library root */
  parentPath?: string;
  /** mergeFolders */
  targetFolderId?: string;
  sourceFolderIds?: string[];
  /** Why the change can't be approved as it stands. */
  error?: string;
}

export const PENDING: IChangeDecision = { status: 'pending' };

function findFolderByPath(scan: IScanResult, path: string): IFolderItem | undefined {
  const wanted: string = path.replace(/^\/+|\/+$/g, '').toLowerCase();
  return values(scan.folders).filter(f => f.path.toLowerCase() === wanted)[0];
}

export function resolveChange(scan: IScanResult, change: IPlannedChange, decision: IChangeDecision = PENDING): IResolvedChange {
  const resolved: IResolvedChange = { change, status: decision.status };

  if (change.type === 'renameFile') {
    const file: IFileItem = scan.files[change.targetId];
    const name: string | undefined = decision.newName ?? change.suggestedName;
    resolved.newName = name;
    if (!name) {
      resolved.error = 'Enter a new name before approving.';
    } else if (name === file.name) {
      resolved.error = 'The new name is the same as the current name.';
    } else {
      resolved.error = validateItemName(name, file.extension);
    }
  } else if (change.type === 'moveFolder') {
    const folder: IFolderItem = scan.folders[change.targetId];
    const parentPath: string = (decision.parentPath ?? change.suggestedParentPath ?? '').replace(/^\/+|\/+$/g, '');
    const parent: IFolderItem | undefined = parentPath ? findFolderByPath(scan, parentPath) : scan.folders[scan.rootId];
    resolved.parentPath = parent ? parent.path : parentPath;
    if (!parent) {
      resolved.error = `No folder named "${parentPath}" exists in this library.`;
    } else if (parent.id === folder.parentId) {
      resolved.error = 'That is already the folder\'s location.';
    } else if (parent.id === folder.id || parent.path.toLowerCase().indexOf(`${folder.path.toLowerCase()}/`) === 0) {
      resolved.error = 'A folder can\'t be moved inside itself.';
    }
  } else if (change.type === 'fileIntake') {
    const file: IFileItem = scan.files[change.targetId];
    const chosen: string | undefined = decision.parentPath ?? change.suggestedParentPath;
    const parentPath: string = (chosen ?? '').replace(/^\/+|\/+$/g, '');
    const parent: IFolderItem | undefined = parentPath ? findFolderByPath(scan, parentPath) : undefined;
    resolved.parentPath = parent ? parent.path : parentPath;
    if (chosen === undefined || !parentPath) {
      resolved.error = 'Choose the folder this file belongs in.';
    } else if (!parent) {
      resolved.error = `No folder named "${parentPath}" exists in this library.`;
    } else if (parent.id === file.parentId) {
      resolved.error = 'Choose a folder other than the intake folder.';
    }
  } else if (change.type === 'restructure') {
    // The destination may be a category folder that doesn't exist yet; it is created on apply.
    const chosen: string | undefined = decision.parentPath ?? change.suggestedParentPath;
    const parentPath: string = (chosen ?? '').replace(/^\/+|\/+$/g, '').replace(/\/{2,}/g, '/');
    const existing: IFolderItem | undefined = parentPath ? findFolderByPath(scan, parentPath) : undefined;
    const current: string = change.itemKind === 'file' ? scan.files[change.targetId].parentPath : parentOf(scan.folders[change.targetId].path);
    const itemPath: string = change.itemKind === 'file'
      ? joinPath(scan.files[change.targetId].parentPath, scan.files[change.targetId].name)
      : scan.folders[change.targetId].path;
    resolved.parentPath = existing ? existing.path : parentPath;
    const currentName: string = change.itemKind === 'file' ? scan.files[change.targetId].name : scan.folders[change.targetId].name;
    const newName: string | undefined = decision.newName ?? change.suggestedName;
    resolved.newName = newName;
    // The top of the library ('') is a valid destination, not an empty folder name.
    const badSegment: string | undefined = parentPath ? parentPath.split('/').map(s => validateItemName(s)).filter(e => !!e)[0] : undefined;
    const badName: string | undefined = newName && newName !== currentName
      ? validateItemName(newName, change.itemKind === 'file' ? scan.files[change.targetId].extension : undefined)
      : undefined;
    if (chosen === undefined || (!parentPath && !newName)) {
      resolved.error = 'Choose the category folder this belongs in.';
    } else if (badSegment) {
      resolved.error = badSegment;
    } else if (badName) {
      resolved.error = badName;
    } else if (parentPath.toLowerCase() === current.toLowerCase() && (!newName || newName === currentName)) {
      resolved.error = 'That is already where it is.';
    } else if (change.itemKind === 'folder' && `${parentPath.toLowerCase()}/`.indexOf(`${itemPath.toLowerCase()}/`) === 0) {
      resolved.error = 'A folder can\'t be moved inside itself.';
    }
  } else if (change.type === 'createFolder') {
    const path: string = (decision.parentPath ?? change.suggestedParentPath ?? '').replace(/^\/+|\/+$/g, '').replace(/\/{2,}/g, '/');
    resolved.parentPath = path;
    const badSegment: string | undefined = path.split('/').map(s => validateItemName(s)).filter(e => !!e)[0];
    if (!path) {
      resolved.error = 'Name the folder to create.';
    } else if (badSegment) {
      resolved.error = badSegment;
    }
  } else if (change.type === 'fileDocument') {
    // Rename to the standard and move into Client/Year/Category, creating folders as needed.
    const file: IFileItem = scan.files[change.targetId];
    const parentPath: string = (decision.parentPath ?? change.suggestedParentPath ?? '').replace(/^\/+|\/+$/g, '').replace(/\/{2,}/g, '/');
    const name: string | undefined = decision.newName ?? change.suggestedName;
    const existing: IFolderItem | undefined = parentPath ? findFolderByPath(scan, parentPath) : undefined;
    resolved.parentPath = existing ? existing.path : parentPath;
    resolved.newName = name;
    const badSegment: string | undefined = parentPath.split('/').map(s => validateItemName(s)).filter(e => !!e)[0];
    if (!parentPath) {
      resolved.error = 'Choose the folder this document belongs in.';
    } else if (badSegment) {
      resolved.error = badSegment;
    } else if (!name) {
      resolved.error = 'Enter a name before approving.';
    } else if (parentPath.toLowerCase() === file.parentPath.toLowerCase() && name === file.name) {
      resolved.error = 'That is already its name and place.';
    } else {
      resolved.error = validateItemName(name, file.extension);
    }
  } else if (change.type === 'mergeFolders') {
    const target: string = decision.targetFolderId ?? change.targetId;
    resolved.targetFolderId = target;
    resolved.sourceFolderIds = (change.candidateFolderIds ?? [change.targetId]).filter(id => id !== target);
  }

  return resolved;
}

function joinPath(parent: string, name: string): string {
  return parent ? `${parent}/${name}` : name;
}

function parentOf(path: string): string {
  const slash: number = path.lastIndexOf('/');
  return slash === -1 ? '' : path.substring(0, slash);
}

function nameOf(path: string): string {
  return path.substring(path.lastIndexOf('/') + 1);
}

/** Explicit steps first-to-last when every change has one; otherwise the fixed order by type. */
export function compareChanges(a: IPlannedChange, b: IPlannedChange): number {
  if (a.step !== undefined && b.step !== undefined) {
    return a.step - b.step;
  }
  return CHANGE_ORDER.indexOf(a.type) - CHANGE_ORDER.indexOf(b.type);
}

/** Order changes are applied in, shared by the preview and the change engine. */
export const CHANGE_ORDER: IPlannedChange['type'][] = ['quarantineFile', 'quarantineFolder', 'mergeFolders', 'moveFolder', 'flattenFolders', 'fileIntake', 'createFolder', 'restructure', 'fileDocument', 'renameFile'];

/**
 * Computes what the library would look like after the given changes, and which files
 * would collide. includeProposed: also apply pending (not yet approved) changes.
 * Folders that land on the same path merge naturally; files that do are conflicts.
 */
export function simulatePlan(
  scan: IScanResult,
  plan: IOrganizationPlan,
  decisions: { [changeId: string]: IChangeDecision },
  includeProposed: boolean
): IPlanPreview {
  const files: IFileItem[] = values(scan.files);
  const folders: IFolderItem[] = values(scan.folders).filter(f => f.id !== scan.rootId);

  const paths: { [id: string]: string } = {};
  for (const f of files) { paths[f.id] = filePath(f); }
  for (const f of folders) { paths[f.id] = f.path; }
  const allIds: string[] = Object.keys(paths);

  const marks: { [id: string]: PreviewMark } = {};
  const touchedBy: { [id: string]: string[] } = {};
  const touch = (id: string, changeId: string, mark?: PreviewMark): void => {
    (touchedBy[id] = touchedBy[id] || []).push(changeId);
    if (mark && !marks[id]) {
      marks[id] = mark;
    }
  };

  /** Re-roots every item at or under `from` to `to`. */
  const movePrefix = (from: string, to: string, changeId: string, mark: PreviewMark, includeSelf: boolean): void => {
    const lower: string = from.toLowerCase();
    for (const id of allIds) {
      const p: string = paths[id];
      const pl: string = p.toLowerCase();
      if ((includeSelf && pl === lower) || pl.indexOf(`${lower}/`) === 0) {
        paths[id] = to + p.substring(from.length);
        touch(id, changeId, mark);
      }
    }
  };

  const active: IResolvedChange[] = plan.changes
    .map(c => resolveChange(scan, c, decisions[c.id]))
    .filter(r => !r.error && (r.status === 'approved' || (includeProposed && r.status === 'pending')))
    .sort((a, b) => compareChanges(a.change, b.change));

  for (const r of active) {
    const c: IPlannedChange = r.change;
    switch (c.type) {
      case 'quarantineFile':
        paths[c.targetId] = joinPath(plan.quarantinePath, paths[c.targetId]);
        touch(c.targetId, c.id, 'quarantined');
        break;
      case 'quarantineFolder': {
        const from: string = paths[c.targetId];
        movePrefix(from, joinPath(plan.quarantinePath, from), c.id, 'quarantined', true);
        break;
      }
      case 'mergeFolders': {
        const target: string = paths[r.targetFolderId!];
        for (const sourceId of r.sourceFolderIds!) {
          const from: string = paths[sourceId];
          movePrefix(from, target, c.id, 'merged', false);
          // The emptied source folder goes to quarantine rather than being deleted.
          paths[sourceId] = joinPath(plan.quarantinePath, from);
          touch(sourceId, c.id, 'quarantined');
        }
        break;
      }
      case 'moveFolder': {
        const from: string = paths[c.targetId];
        const parent: IFolderItem | undefined = r.parentPath ? findFolderByPath(scan, r.parentPath) : undefined;
        const parentNow: string = parent ? paths[parent.id] : '';
        movePrefix(from, joinPath(parentNow, nameOf(from)), c.id, 'moved', true);
        break;
      }
      case 'flattenFolders':
        for (const id of c.collapseFolderIds ?? []) {
          const childId: string = scan.folders[id].childFolderIds[0];
          const childPath: string = paths[childId];
          movePrefix(childPath, joinPath(parentOf(paths[id]), nameOf(childPath)), c.id, 'moved', true);
          paths[id] = joinPath(plan.quarantinePath, scan.folders[id].path);
          touch(id, c.id, 'quarantined');
        }
        break;
      case 'fileIntake': {
        const parent: IFolderItem | undefined = findFolderByPath(scan, r.parentPath!);
        paths[c.targetId] = joinPath(parent ? paths[parent.id] ?? parent.path : r.parentPath!, nameOf(paths[c.targetId]));
        touch(c.targetId, c.id, 'moved');
        break;
      }
      case 'restructure': {
        // New category folders appear in the preview implicitly, from the moved items' paths.
        const from: string = paths[c.targetId];
        movePrefix(from, joinPath(r.parentPath!, r.newName || nameOf(from)), c.id, 'moved', true);
        break;
      }
      case 'createFolder':
        break;
      case 'fileDocument':
        paths[c.targetId] = joinPath(r.parentPath!, r.newName!);
        touch(c.targetId, c.id, 'moved');
        break;
      case 'renameFile':
        paths[c.targetId] = joinPath(parentOf(paths[c.targetId]), r.newName!);
        touch(c.targetId, c.id, 'renamed');
        break;
    }
  }

  // SharePoint paths are case-insensitive.
  const byPath: { [path: string]: string[] } = {};
  for (const f of files) {
    const key: string = paths[f.id].toLowerCase();
    (byPath[key] = byPath[key] || []).push(f.id);
  }
  const conflicts: IPlanConflict[] = [];
  for (const key of Object.keys(byPath)) {
    const ids: string[] = byPath[key];
    if (ids.length < 2) {
      continue;
    }
    const changeIds: string[] = [];
    for (const id of ids) {
      for (const changeId of touchedBy[id] || []) {
        if (changeIds.indexOf(changeId) === -1) {
          changeIds.push(changeId);
        }
      }
    }
    conflicts.push({
      changeIds,
      message: `${ids.length} files would end up at "${paths[ids[0]]}": ` +
        ids.map(id => `"${filePath(scan.files[id])}"`).join(', ') +
        '. Reject one of the changes, set a copy aside, or rename one of them.'
    });
  }

  const entries: IPreviewEntry[] = [];
  for (const f of files) {
    entries.push({ id: f.id, kind: 'file', beforePath: filePath(f), afterPath: paths[f.id], mark: marks[f.id] });
  }
  for (const f of folders) {
    entries.push({ id: f.id, kind: 'folder', beforePath: folderLabel(f), afterPath: paths[f.id], mark: marks[f.id] });
  }

  return { entries, conflicts };
}
